import React, { useState, useEffect } from 'react';
import { MapPin, Search, Phone, ChevronRight, Building, Stethoscope, GraduationCap, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const getServiceIcon = (type) => {
  switch (type) {
    case 'Santé': return Stethoscope;
    case 'Éducation': return GraduationCap;
    default: return Building;
  }
};

const TrouverService = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('Tous');
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchServices = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/services`, { signal: controller.signal });
        if (isMounted) setServices(res.data);
      } catch (e) {
        if (e.name !== 'CanceledError') {
          if (isMounted) setError(true);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchServices();
    return () => { isMounted = false; controller.abort(); };
  }, []);

  const filteredServices = services.filter(service => {
    const matchesSearch = service.nom.toLowerCase().includes(searchTerm.toLowerCase()) || service.adresse.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'Tous' || service.type === filterType;
    return matchesSearch && matchesType;
  });

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="bg-[#0A192F] text-white py-12 px-4 border-b border-slate-800">
        <div className="container mx-auto max-w-5xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-900 text-blue-200 rounded-xl flex items-center justify-center">
              <MapPin className="w-5 h-5" />
            </div>
            <h1 className="text-3xl font-bold">Annuaire des Services</h1>
          </div>
          <p className="text-blue-200 text-lg max-w-2xl mb-8">
            Trouvez les coordonnées et adresses des structures médicales, sociales et administratives de la province du Lualaba.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 bg-white/10 p-2 rounded-xl backdrop-blur-sm border border-white/10 overflow-x-auto hide-scrollbar">
            <div className="relative flex-grow min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input 
                type="text" 
                placeholder="Rechercher un hôpital, une école, une adresse..." 
                className="w-full pl-10 pr-4 py-3 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-service-input"
              />
            </div>
            <select 
              className="py-3 px-4 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white sm:w-48 border-none shrink-0"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              data-testid="filter-service-select"
            >
              <option value="Tous">Tous les secteurs</option>
              <option value="Santé">Santé</option>
              <option value="Éducation">Éducation</option>
              <option value="Social">Action Sociale</option>
              <option value="Administration">Administration</option>
            </select>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-5xl px-4 py-12">
        <div className="mb-6 p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r text-amber-800 text-sm font-medium flex items-start gap-3">
          <Building className="w-5 h-5 shrink-0 mt-0.5" />
          <p>
            L'annuaire recense les structures partenaires. Les fiches sont mises à jour par les services d'inspection.
          </p>
        </div>
        
        {loading ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
            <Loader2 className="w-10 h-10 animate-spin text-blue-900 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">Chargement de l'annuaire en cours...</p>
          </div>
        ) : error ? (
           <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Erreur de connexion</h3>
            <p className="text-slate-500 mb-4">Impossible de récupérer les données de l'annuaire.</p>
            <Button onClick={() => window.location.reload()} variant="outline">Réessayer</Button>
          </div>
        ) : filteredServices.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
            <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-900 mb-1">Aucun service trouvé</h3>
            <p className="text-slate-500">Essayez de modifier vos critères de recherche.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredServices.map(service => {
              const Icon = getServiceIcon(service.type);
              const isVerified = service.statut_verification && service.statut_verification.includes('Vérifié');
              
              return (
              <div key={service.id} className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow group flex flex-col" data-testid={`service-card-${service.id}`}>
                <div className="flex justify-between items-start mb-4">
                  <div className={`p-3 rounded-xl flex-shrink-0 ${
                    service.type === 'Santé' ? 'bg-rose-50 text-rose-600' :
                    service.type === 'Éducation' ? 'bg-blue-50 text-blue-600' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  {isVerified ? (
                    <span className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md border border-emerald-100">
                      <CheckCircle2 className="w-3 h-3" /> Vérifié
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 px-2 py-1 rounded-md border border-slate-200">
                      <AlertCircle className="w-3 h-3" /> À confirmer
                    </span>
                  )}
                </div>
                
                <h3 className="text-lg font-bold text-slate-900 mb-1 group-hover:text-blue-700 transition-colors">{service.nom}</h3>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4 inline-block">{service.type}</span>
                
                <div className="mt-auto space-y-3 pt-4 border-t border-slate-100">
                  <div className="flex items-start gap-3 text-sm text-slate-600">
                    <MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                    <span>{service.adresse}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-slate-600">
                    <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                    <span className={service.tel === "Non renseigné" ? "text-slate-400 italic" : "font-medium"}>
                      {service.tel}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 pt-2 border-t border-slate-50 border-dashed">
                    <span className="italic">{service.statut_verification === "Non renseigné" ? "Vérification en attente" : service.statut_verification}</span>
                  </div>
                </div>
              </div>
            )})}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrouverService;
